import React, { useState, useEffect, useMemo } from 'react';
import { polyfill } from 'mobile-drag-drop';
import { scrollBehaviourDragImageTranslateOverride } from 'mobile-drag-drop/scroll-behaviour';
import 'mobile-drag-drop/default.css';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  IconButton,
  Button,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  Fade,
  CircularProgress,
  Alert,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Skeleton,
  Chip,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Autocomplete,
  Snackbar,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  MoreVert as MoreIcon,
  CreateNewFolder as NewFolderIcon,
  UploadFile as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DriveFileMove as MoveIcon,
  Download as DownloadIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  FilterAltOff as FilterOffIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Movie as VideoIcon,
  MusicNote as AudioIcon,
  TableChart as SpreadsheetIcon,
  Slideshow as PresentationIcon,
  Description as DocIcon,
  Archive as ArchiveIcon,
  Close as CloseIcon,
  SortByAlpha as SortIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import useStore from '../store/useStore';
import { translations } from '../translations';

// ── Mobile drag polyfill ──────────────────────────────────────────────────────
polyfill({
  dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride,
  holdToDrag: 400,
});

// ── Helpers ───────────────────────────────────────────────────────────────────
function getFileType(mimeType, name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  const m = mimeType || '';
  if (
    m.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)
  )
    return { color: '#16a34a', Icon: ImageIcon, label: 'Изображение' };
  if (
    m.startsWith('video/') ||
    ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv'].includes(ext)
  )
    return { color: '#7c3aed', Icon: VideoIcon, label: 'Видео' };
  if (
    m.startsWith('audio/') ||
    ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'].includes(ext)
  )
    return { color: '#d97706', Icon: AudioIcon, label: 'Аудио' };
  if (m === 'application/pdf' || ext === 'pdf')
    return { color: '#dc2626', Icon: PdfIcon, label: 'PDF' };
  if (
    m.includes('word') ||
    m.includes('document') ||
    ['doc', 'docx', 'odt', 'rtf'].includes(ext)
  )
    return { color: '#2563eb', Icon: DocIcon, label: 'Документ' };
  if (
    m.includes('excel') ||
    m.includes('spreadsheet') ||
    ['xls', 'xlsx', 'csv', 'ods'].includes(ext)
  )
    return { color: '#059669', Icon: SpreadsheetIcon, label: 'Таблица' };
  if (
    m.includes('powerpoint') ||
    m.includes('presentation') ||
    ['ppt', 'pptx', 'odp'].includes(ext)
  )
    return { color: '#ea580c', Icon: PresentationIcon, label: 'Презентация' };
  if (
    m.includes('zip') ||
    m.includes('compressed') ||
    m.includes('archive') ||
    ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)
  )
    return { color: '#78716c', Icon: ArchiveIcon, label: 'Архив' };
  return { color: '#C2233E', Icon: FileIcon, label: 'Файл' };
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function canPreview(mimeType, name) {
  const { label } = getFileType(mimeType, name);
  return ['Изображение', 'Видео', 'Аудио', 'PDF'].includes(label);
}

const teacherName = (tch) => (tch && (tch.displayName || tch.username)) || '';

// Who can see an item, for the chip on each card (admin view).
function accessLabel(item, teacherById) {
  if (item.isPublic)
    return { text: 'Все учителя', Icon: PublicIcon, color: 'default' };
  const names = (item.allowedTeachers || [])
    .map((id) => teacherName(teacherById.get(String(id))))
    .filter(Boolean);
  if (!names.length)
    return { text: 'Только админ', Icon: LockIcon, color: 'warning' };
  const shown = names.slice(0, 2).join(', ');
  return {
    text: names.length > 2 ? `${shown} +${names.length - 2}` : shown,
    Icon: LockIcon,
    color: 'primary',
  };
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function ResourceLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [path, setPath] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Search & filters. Search, teacher and access filters look through the
  // whole library (not just the open folder); `found` holds those results.
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('type-name');
  const [teacherFilter, setTeacherFilter] = useState([]);
  const [accessFilter, setAccessFilter] = useState('');
  const [found, setFound] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [notice, setNotice] = useState('');

  // Selection (admin): ticking any card shows the bulk-action bar
  const [selectedIds, setSelectedIds] = useState([]);
  const hasSelection = selectedIds.length > 0;

  // Dialogs
  const [openFolderDialog, setOpenFolderDialog] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [moveIds, setMoveIds] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    bulk: false,
  });
  const [previewItem, setPreviewItem] = useState(null);

  const [allFolders, setAllFolders] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [folderName, setFolderName] = useState('');
  const [newName, setNewName] = useState('');
  // Access dialog for one or many items: mode public/private/keep, per-teacher
  // state on/off/mixed (mixed = some of the selected items have the teacher)
  const [accessTargets, setAccessTargets] = useState(null);
  const [accessForm, setAccessForm] = useState({
    mode: 'public',
    teachers: {},
    recursive: true,
  });
  const [accessInitial, setAccessInitial] = useState(null);
  const [accessSaving, setAccessSaving] = useState(false);

  // Progress dialogs
  const [progressDialog, setProgressDialog] = useState({
    open: false,
    type: 'upload',
    progress: 0,
    title: '',
    items: [],
  });
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);

  const { userRole, language } = useStore();
  const t = translations[language];
  const isAdmin = userRole === 'admin';
  const filtering = Boolean(
    search.trim() || teacherFilter.length || accessFilter
  );

  // Teachers who can be given access (accounts holding the teacher role)
  const staffTeachers = useMemo(
    () =>
      teachers
        .filter((tch) =>
          (Array.isArray(tch.roles) && tch.roles.length
            ? tch.roles
            : [tch.role]
          ).includes('teacher')
        )
        .sort((a, b) => teacherName(a).localeCompare(teacherName(b), 'ru')),
    [teachers]
  );
  const teacherById = useMemo(
    () => new Map(teachers.map((tch) => [String(tch._id), tch])),
    [teachers]
  );

  // ── Filtered + sorted items ─────────────────────────────────────────────────
  const displayItems = useMemo(() => {
    const result = filtering ? found || [] : items;
    // library-wide results come grouped by folder; keep that for the default sort
    if (filtering && sort === 'type-name') return result;
    return [...result].sort((a, b) => {
      // Folders always first except when sorting by name-desc
      if (sort !== 'name-desc') {
        if (a.type === 'folder' && b.type !== 'folder') return -1;
        if (a.type !== 'folder' && b.type === 'folder') return 1;
      }
      switch (sort) {
        case 'name-asc':
          return a.name.localeCompare(b.name, 'ru');
        case 'name-desc':
          return b.name.localeCompare(a.name, 'ru');
        case 'date-desc':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case 'date-asc':
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case 'size-desc':
          return (b.size || 0) - (a.size || 0);
        case 'size-asc':
          return (a.size || 0) - (b.size || 0);
        default:
          return a.name.localeCompare(b.name, 'ru');
      }
    });
  }, [items, found, filtering, sort]);

  // ── Effects ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleGlobalContextMenu = (e) => {
      if (draggedItem) e.preventDefault();
    };
    window.addEventListener('contextmenu', handleGlobalContextMenu);
    return () =>
      window.removeEventListener('contextmenu', handleGlobalContextMenu);
  }, [draggedItem]);

  useEffect(() => {
    fetchItems();
    if (isAdmin) {
      fetchTeachers();
      fetchAllFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolder, isAdmin]);

  // Library-wide search / filters (debounced)
  useEffect(() => {
    setSelectedIds([]);
    if (!filtering) {
      setFound(null);
      return undefined;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      if (teacherFilter.length) params.set('teachers', teacherFilter.join(','));
      if (accessFilter) params.set('access', accessFilter);
      setLoading(true);
      try {
        const res = await fetch(`/api/materials?${params}`, {
          credentials: 'include',
          signal: ctrl.signal,
        });
        setFound(res.ok ? await res.json() : []);
      } catch (err) {
        if (err.name !== 'AbortError') setFound([]);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [filtering, search, teacherFilter, accessFilter, refreshKey]);

  // ── Data fetching ───────────────────────────────────────────────────────────
  const fetchItems = async () => {
    setLoading(true);
    const parentQuery = currentFolder ? `?parentId=${currentFolder}` : '';
    try {
      const res = await fetch(`/api/materials${parentQuery}`, {
        credentials: 'include',
      });
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
      setSelectedIds([]);
    }
  };

  // After a change: refresh whatever is on screen (folder or search results)
  const reload = () => {
    setSelectedIds([]);
    if (filtering) setRefreshKey((k) => k + 1);
    fetchItems();
    if (isAdmin) fetchAllFolders();
  };

  const clearFilters = () => {
    setSearch('');
    setTeacherFilter([]);
    setAccessFilter('');
  };

  const fetchTeachers = async () => {
    const res = await fetch('/api/admin/teachers', { credentials: 'include' });
    if (!res.ok) return;
    const data = await res.json();
    setTeachers(Array.isArray(data) ? data : []);
  };

  const fetchAllFolders = async () => {
    const res = await fetch('/api/materials/all-folders', {
      credentials: 'include',
    });
    if (res.ok) setAllFolders(await res.json());
  };

  // ── Selection ───────────────────────────────────────────────────────────────
  const handleSelectAll = () => setSelectedIds(displayItems.map((i) => i._id));
  const handleDeselectAll = () => setSelectedIds([]);
  const selectedItems = displayItems.filter((i) => selectedIds.includes(i._id));
  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );

  // ── Delete (now with confirmation dialog) ───────────────────────────────────
  const handleDeleteConfirmed = async () => {
    const bulk = deleteDialog.bulk;
    setDeleteDialog({ open: false, bulk: false });

    if (bulk) {
      setProgressDialog({
        open: true,
        type: 'delete',
        progress: 0,
        title: 'Удаление...',
        items: selectedItems.map((i) => i.name),
      });
      let p = 0;
      const interval = setInterval(() => {
        p += 5;
        if (p <= 90) setProgressDialog((prev) => ({ ...prev, progress: p }));
      }, 100);
      const res = await fetch('/api/materials/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds }),
        credentials: 'include',
      });
      clearInterval(interval);
      if (res.ok) {
        setProgressDialog((prev) => ({
          ...prev,
          progress: 100,
          title: 'Удалено!',
        }));
        setTimeout(() => {
          setProgressDialog((prev) => ({ ...prev, open: false }));
          reload();
        }, 1500);
      } else {
        setProgressDialog((prev) => ({ ...prev, open: false }));
      }
    } else {
      setProgressDialog({
        open: true,
        type: 'delete',
        progress: 0,
        title: `Удаление ${selectedItem.name}...`,
        items: [selectedItem.name],
      });
      const res = await fetch(`/api/materials/${selectedItem._id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setProgressDialog((prev) => ({
          ...prev,
          progress: 100,
          title: 'Удалено!',
        }));
        setTimeout(() => {
          setProgressDialog((prev) => ({ ...prev, open: false }));
          handleMenuClose();
          reload();
        }, 1000);
      } else {
        setProgressDialog((prev) => ({ ...prev, open: false }));
      }
    }
  };

  // ── Access, rename, move ────────────────────────────────────────────────────
  const openAccess = (targets) => {
    const state = {};
    staffTeachers.forEach((tch) => {
      const n = targets.filter((it) =>
        (it.allowedTeachers || []).map(String).includes(String(tch._id))
      ).length;
      state[tch._id] = n === 0 ? 'off' : n === targets.length ? 'on' : 'mixed';
    });
    const pub = targets.filter((it) => it.isPublic).length;
    const form = {
      mode: pub === targets.length ? 'public' : pub === 0 ? 'private' : 'keep',
      teachers: state,
      recursive: true,
    };
    setAccessInitial(form);
    setAccessForm(form);
    setAccessTargets(targets);
  };

  // Only what the admin actually changed is sent — untouched teachers stay
  // as they are on every item (and inside folders).
  const accessChanges = () => {
    if (!accessInitial) return null;
    const add = [];
    const remove = [];
    Object.entries(accessForm.teachers).forEach(([id, st]) => {
      if (st === accessInitial.teachers[id]) return;
      if (st === 'on') add.push(id);
      if (st === 'off') remove.push(id);
    });
    const body = { add, remove };
    if (accessForm.mode !== 'keep' && accessForm.mode !== accessInitial.mode)
      body.isPublic = accessForm.mode === 'public';
    const changed = add.length || remove.length || 'isPublic' in body;
    return changed ? body : null;
  };

  const handleSaveAccess = async () => {
    const changes = accessChanges();
    if (!changes || !accessTargets) return;
    setAccessSaving(true);
    try {
      const res = await fetch('/api/materials/bulk-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...changes,
          ids: accessTargets.map((i) => i._id),
          recursive: accessForm.recursive,
        }),
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Не удалось сохранить');
      setAccessTargets(null);
      setNotice(`Доступ обновлён · ${data.updated} эл.`);
      reload();
    } catch (err) {
      setNotice(err.message);
    } finally {
      setAccessSaving(false);
    }
  };

  const handleCreateFolder = async () => {
    const res = await fetch('/api/materials/folder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: folderName, parentId: currentFolder }),
      credentials: 'include',
    });
    if (res.ok) {
      setOpenFolderDialog(false);
      setFolderName('');
      fetchItems();
    }
  };

  const handleMove = async (targetFolderId) => {
    const res = await fetch('/api/materials/bulk-move', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: moveIds, targetParentId: targetFolderId }),
      credentials: 'include',
    });
    if (res.ok) {
      setNotice(
        moveIds.length > 1 ? `Перемещено: ${moveIds.length}` : 'Перемещено'
      );
      setMoveIds(null);
      handleMenuClose();
      reload();
    }
  };

  const handleRename = async () => {
    const res = await fetch(`/api/materials/${selectedItem._id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName }),
      credentials: 'include',
    });
    if (res.ok) {
      setOpenRenameDialog(false);
      setNewName('');
      reload();
    }
  };

  // ── Upload ──────────────────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;
    setUploadFiles(files.map((f) => f.name));
    setOpenUploadDialog(true);
    setUploading(true);
    setUploadProgress(0);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const completedBeforeCurrent = i;
      const formData = new FormData();
      formData.append('files', file);
      if (currentFolder) formData.append('parentId', currentFolder);
      try {
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', '/api/materials/upload');
          xhr.withCredentials = true;
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const totalPercent = Math.round(
                (completedBeforeCurrent / files.length) * 100 +
                  ((event.loaded / event.total) * 100) / files.length
              );
              setUploadProgress(totalPercent);
            }
          };
          xhr.onload = () =>
            xhr.status === 200 || xhr.status === 201
              ? resolve()
              : reject(new Error('Upload failed'));
          xhr.onerror = () => reject(new Error('Upload network error'));
          xhr.send(formData);
        });
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }
    setUploadProgress(100);
    setTimeout(() => {
      setUploading(false);
      setOpenUploadDialog(false);
      setUploadFiles([]);
      fetchItems();
    }, 1000);
  };

  // ── Menu ────────────────────────────────────────────────────────────────────
  const handleMenuOpen = (event, item) => {
    setSelectedItem(item);
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const navigateToFolder = (item) => {
    // From library-wide results the folder's own chain is used, not the open path
    const base = filtering && item.pathItems ? item.pathItems : path;
    if (filtering) clearFilters();
    setPath([...base, { id: item._id, name: item.name }]);
    setCurrentFolder(item._id);
  };

  // Open in the folder where a search result lives
  const openContainingFolder = (item) => {
    const chain = item.pathItems || [];
    clearFilters();
    setPath(chain);
    setCurrentFolder(chain.length ? chain[chain.length - 1].id : null);
  };

  const openItem = (item) => {
    if (item.type === 'folder') navigateToFolder(item);
    else if (canPreview(item.mimeType, item.name)) setPreviewItem(item);
    else window.open(item.url);
  };

  const navigateUp = (index) => {
    if (index === -1) {
      setPath([]);
      setCurrentFolder(null);
    } else {
      const newPath = path.slice(0, index + 1);
      setPath(newPath);
      setCurrentFolder(newPath[newPath.length - 1].id);
    }
  };

  // ── Drag & drop ─────────────────────────────────────────────────────────────
  const onDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.setData('itemId', item._id);
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    if (dragOverId !== id) setDragOverId(id);
  };
  const onDragLeave = () => setDragOverId(null);

  const onDrop = async (e, targetFolderId) => {
    e.preventDefault();
    setDragOverId(null);
    const itemId =
      e.dataTransfer.getData('itemId') ||
      (draggedItem ? draggedItem._id : null);
    if (!itemId || itemId === targetFolderId) return;
    const res = await fetch(`/api/materials/${itemId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentId: targetFolderId }),
      credentials: 'include',
    });
    if (res.ok) {
      setDraggedItem(null);
      fetchItems();
    }
  };

  const onDropToRoot = async (e) => {
    e.preventDefault();
    setDragOverId(null);
    const itemId =
      e.dataTransfer.getData('itemId') ||
      (draggedItem ? draggedItem._id : null);
    if (!itemId) return;
    const res = await fetch(`/api/materials/${itemId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentId: null }),
      credentials: 'include',
    });
    if (res.ok) {
      setDraggedItem(null);
      fetchItems();
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Layout title={t.resources}>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* ── Header ── */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: 'primary.main',
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              }}
            >
              {t.resources}
            </Typography>
            <Breadcrumbs
              sx={{
                '& .MuiBreadcrumbs-ol': {
                  flexWrap: 'nowrap',
                  overflowX: 'auto',
                  pb: 0.5,
                },
              }}
            >
              <Link
                component="button"
                underline="hover"
                color="inherit"
                onClick={() => navigateUp(-1)}
                onDragOver={(e) => onDragOver(e, 'root')}
                onDragLeave={onDragLeave}
                onDrop={onDropToRoot}
                sx={{
                  fontWeight: path.length === 0 ? 700 : 400,
                  whiteSpace: 'nowrap',
                  px: 1,
                  borderRadius: 1,
                  bgcolor:
                    dragOverId === 'root' ? 'action.hover' : 'transparent',
                  border:
                    dragOverId === 'root'
                      ? '1px dashed'
                      : '1px solid transparent',
                  borderColor: 'primary.main',
                }}
              >
                Корень
              </Link>
              {path.map((p, i) => (
                <Link
                  key={p.id}
                  component="button"
                  underline="hover"
                  color="inherit"
                  onClick={() => navigateUp(i)}
                  onDragOver={(e) => onDragOver(e, p.id)}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => onDrop(e, p.id)}
                  sx={{
                    fontWeight: i === path.length - 1 ? 700 : 400,
                    whiteSpace: 'nowrap',
                    px: 1,
                    borderRadius: 1,
                    bgcolor:
                      dragOverId === p.id ? 'action.hover' : 'transparent',
                    border:
                      dragOverId === p.id
                        ? '1px dashed'
                        : '1px solid transparent',
                    borderColor: 'primary.main',
                  }}
                >
                  {p.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>

          {isAdmin && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<NewFolderIcon />}
                onClick={() => setOpenFolderDialog(true)}
                sx={{ flex: { xs: 1, sm: 'none' }, whiteSpace: 'nowrap' }}
              >
                Новая папка
              </Button>
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Загрузить
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileUpload}
                />
              </Button>
            </Box>
          )}
        </Box>

        {/* ── Search + filters ── */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            mb: 1.5,
            flexWrap: 'wrap',
            alignItems: 'flex-start',
          }}
        >
          <TextField
            size="small"
            placeholder="Поиск по всей библиотеке: название, код урока, тема…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: '1 1 260px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {isAdmin && (
            <Autocomplete
              multiple
              size="small"
              options={staffTeachers}
              value={staffTeachers.filter((tch) =>
                teacherFilter.includes(tch._id)
              )}
              onChange={(_e, value) =>
                setTeacherFilter(value.map((tch) => tch._id))
              }
              getOptionLabel={teacherName}
              isOptionEqualToValue={(a, b) => a._id === b._id}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option._id}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                  {teacherName(option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Назначено учителям"
                  placeholder={teacherFilter.length ? '' : 'Любой учитель'}
                />
              )}
              sx={{ flex: '1 1 240px', maxWidth: { sm: 380 } }}
            />
          )}
          {isAdmin && (
            <FormControl size="small" sx={{ flex: '0 1 190px', minWidth: 170 }}>
              <InputLabel>Доступ</InputLabel>
              <Select
                value={accessFilter}
                label="Доступ"
                onChange={(e) => setAccessFilter(e.target.value)}
              >
                <MenuItem value="">Любой</MenuItem>
                <MenuItem value="public">Видно всем учителям</MenuItem>
                <MenuItem value="private">Ограниченный</MenuItem>
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ flex: '0 1 190px', minWidth: 170 }}>
            <InputLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <SortIcon fontSize="small" />
                Сортировка
              </Box>
            </InputLabel>
            <Select
              value={sort}
              label="Сортировка"
              onChange={(e) => setSort(e.target.value)}
            >
              <MenuItem value="type-name">
                {filtering ? 'По папкам' : 'Папки → по имени'}
              </MenuItem>
              <MenuItem value="name-asc">Имя (А → Я)</MenuItem>
              <MenuItem value="name-desc">Имя (Я → А)</MenuItem>
              <MenuItem value="date-desc">Новые сначала</MenuItem>
              <MenuItem value="date-asc">Старые сначала</MenuItem>
              <MenuItem value="size-desc">Размер (больше)</MenuItem>
              <MenuItem value="size-asc">Размер (меньше)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* ── Result line: what is shown + select all ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            minHeight: 36,
            flexWrap: 'wrap',
          }}
        >
          {isAdmin && displayItems.length > 0 && (
            <FormControlLabel
              sx={{ mr: 1 }}
              control={
                <Checkbox
                  size="small"
                  checked={
                    selectedIds.length > 0 &&
                    selectedIds.length === displayItems.length
                  }
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < displayItems.length
                  }
                  onChange={(e) =>
                    e.target.checked ? handleSelectAll() : handleDeselectAll()
                  }
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Выбрать все
                </Typography>
              }
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {filtering
              ? loading && !found
                ? 'Ищем по всей библиотеке…'
                : `Найдено по всей библиотеке: ${displayItems.length}${displayItems.length >= 500 ? '+' : ''}`
              : `${displayItems.length} в этой папке`}
          </Typography>
          {filtering && (
            <Button
              size="small"
              startIcon={<FilterOffIcon />}
              onClick={clearFilters}
            >
              Сбросить
            </Button>
          )}
        </Box>

        {/* ── Loading bar ── */}
        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        {/* ── Grid ── */}
        <Grid container spacing={2}>
          {!loading && displayItems.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Paper
                sx={{
                  p: { xs: 4, md: 10 },
                  textAlign: 'center',
                  bgcolor: 'transparent',
                  border: '2px dashed',
                  borderColor: 'divider',
                }}
              >
                <Typography color="text.secondary">
                  {filtering ? 'Ничего не найдено' : 'Папка пуста'}
                </Typography>
              </Paper>
            </Grid>
          )}

          {loading &&
            !displayItems.length &&
            Array.from({ length: 8 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
                <Skeleton
                  variant="rounded"
                  height={80}
                  sx={{ borderRadius: 3 }}
                />
              </Grid>
            ))}

          {displayItems.map((item) => {
            const ft =
              item.type === 'folder'
                ? null
                : getFileType(item.mimeType, item.name);
            const Icon = item.type === 'folder' ? FolderIcon : ft.Icon;
            const iconColor = item.type === 'folder' ? '#f59e0b' : ft.color;
            const checked = selectedIds.includes(item._id);
            const access = isAdmin ? accessLabel(item, teacherById) : null;
            const canDrag = isAdmin && !hasSelection && !filtering;

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item._id}>
                <Card
                  variant="outlined"
                  draggable={canDrag}
                  onDragStart={(e) => onDragStart(e, item)}
                  onDragEnd={() => {
                    setDraggedItem(null);
                    setDragOverId(null);
                  }}
                  onDragOver={
                    item.type === 'folder' && canDrag
                      ? (e) => onDragOver(e, item._id)
                      : undefined
                  }
                  onDragLeave={item.type === 'folder' ? onDragLeave : undefined}
                  onDrop={
                    item.type === 'folder' && canDrag
                      ? (e) => onDrop(e, item._id)
                      : undefined
                  }
                  onContextMenu={(e) => {
                    if (isAdmin && window.innerWidth < 600) e.preventDefault();
                  }}
                  onClick={() =>
                    hasSelection ? toggleSelect(item._id) : openItem(item)
                  }
                  sx={{
                    borderRadius: 3,
                    transition: '0.2s',
                    position: 'relative',
                    height: '100%',
                    touchAction: canDrag ? 'none' : 'auto',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.07)',
                      borderColor: 'primary.main',
                    },
                    bgcolor: checked
                      ? 'action.selected'
                      : draggedItem?._id === item._id
                        ? 'action.selected'
                        : dragOverId === item._id
                          ? 'action.hover'
                          : 'background.paper',
                    borderStyle: dragOverId === item._id ? 'dashed' : 'solid',
                    borderWidth: 2,
                    borderColor:
                      checked || dragOverId === item._id
                        ? 'primary.main'
                        : 'divider',
                    opacity: draggedItem?._id === item._id ? 0.5 : 1,
                    '& *': {
                      pointerEvents: dragOverId ? 'none' : 'auto',
                    },
                  }}
                >
                  <CardContent sx={{ p: '10px 8px 10px 4px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isAdmin ? (
                        <Checkbox
                          size="small"
                          checked={checked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSelect(item._id)}
                          inputProps={{ 'aria-label': `Выбрать ${item.name}` }}
                          sx={{ p: 0.75 }}
                        />
                      ) : (
                        <Box sx={{ width: 4 }} />
                      )}
                      <Icon
                        sx={{
                          fontSize: { xs: 32, sm: 36 },
                          color: iconColor,
                          flexShrink: 0,
                        }}
                      />

                      {/* Name + meta */}
                      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        <Tooltip title={item.name} enterDelay={600}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Tooltip>
                        {filtering && (
                          <Typography
                            variant="caption"
                            color="primary"
                            noWrap
                            component="div"
                            onClick={(e) => {
                              e.stopPropagation();
                              openContainingFolder(item);
                            }}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {item.folderPath || 'Корень'}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: 'block' }}
                        >
                          {item.type === 'folder'
                            ? 'Папка'
                            : [
                                ft.label,
                                fmtSize(item.size),
                                fmtDate(item.createdAt),
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                        </Typography>
                        {access && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={access.color}
                            icon={<access.Icon sx={{ fontSize: 14 }} />}
                            label={access.text}
                            onClick={(e) => {
                              e.stopPropagation();
                              openAccess([item]);
                            }}
                            sx={{
                              mt: 0.5,
                              height: 22,
                              maxWidth: '100%',
                              fontSize: 11,
                            }}
                          />
                        )}
                      </Box>

                      {/* Actions */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {item.type === 'file' && (
                          <Tooltip title="Скачать">
                            <IconButton
                              size="small"
                              component="a"
                              href={item.url}
                              download={item.name}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isAdmin && (
                          <IconButton
                            size="small"
                            aria-label="Действия"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(e, item);
                            }}
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* ── Bulk actions for the ticked items ── */}
        {isAdmin && hasSelection && (
          <Paper
            elevation={8}
            sx={{
              position: 'sticky',
              bottom: { xs: 72, md: 16 },
              zIndex: 5,
              mt: 2,
              p: 1,
              pl: 2,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid',
              borderColor: 'primary.main',
              // phones: secondary actions become icons so the bar stays one row
              '& .bulk-label': { display: { xs: 'none', sm: 'inline' } },
              '& .bulk-secondary': {
                minWidth: { xs: 40, sm: 64 },
                px: { xs: 1, sm: 1.5 },
              },
              '& .bulk-secondary .MuiButton-startIcon': {
                mx: { xs: 0, sm: undefined },
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 800, mr: 'auto', whiteSpace: 'nowrap' }}
            >
              Выбрано: {selectedIds.length}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<LockIcon />}
              onClick={() => openAccess(selectedItems)}
            >
              Доступ
            </Button>
            <Tooltip title="Переместить">
              <Button
                size="small"
                variant="outlined"
                className="bulk-secondary"
                aria-label="Переместить"
                startIcon={<MoveIcon />}
                onClick={() => setMoveIds(selectedIds)}
              >
                <span className="bulk-label">Переместить</span>
              </Button>
            </Tooltip>
            <Tooltip title="Удалить">
              <Button
                size="small"
                variant="outlined"
                color="error"
                className="bulk-secondary"
                aria-label="Удалить"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialog({ open: true, bulk: true })}
              >
                <span className="bulk-label">Удалить</span>
              </Button>
            </Tooltip>
            <IconButton
              size="small"
              aria-label="Снять выбор"
              onClick={handleDeselectAll}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Paper>
        )}

        <Snackbar
          open={Boolean(notice)}
          autoHideDuration={3500}
          onClose={() => setNotice('')}
          message={notice}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />

        {/* ── Context menu (admin) ── */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {selectedItem?.type === 'file' &&
            canPreview(selectedItem?.mimeType, selectedItem?.name) && (
              <MenuItem
                onClick={() => {
                  setPreviewItem(selectedItem);
                  handleMenuClose();
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 18, mr: 1 }} /> Просмотр
              </MenuItem>
            )}
          <MenuItem
            onClick={() => {
              setNewName(selectedItem.name);
              setOpenRenameDialog(true);
              handleMenuClose();
            }}
          >
            <EditIcon sx={{ fontSize: 18, mr: 1 }} /> Переименовать
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMoveIds([selectedItem._id]);
              handleMenuClose();
            }}
          >
            <MoveIcon sx={{ fontSize: 18, mr: 1 }} /> Переместить
          </MenuItem>
          <MenuItem
            onClick={() => {
              openAccess([selectedItem]);
              handleMenuClose();
            }}
          >
            <LockIcon sx={{ fontSize: 18, mr: 1 }} /> Доступ
          </MenuItem>
          {selectedItem?.type === 'file' && (
            <MenuItem
              component="a"
              href={selectedItem?.url}
              download={selectedItem?.name}
              onClick={handleMenuClose}
            >
              <DownloadIcon sx={{ fontSize: 18, mr: 1 }} /> Скачать
            </MenuItem>
          )}
          <Divider />
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              handleMenuClose();
              setDeleteDialog({ open: true, bulk: false });
            }}
          >
            <DeleteIcon sx={{ fontSize: 18, mr: 1 }} /> Удалить
          </MenuItem>
        </Menu>

        {/* ── Delete confirmation dialog ── */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, bulk: false })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Подтвердите удаление
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {deleteDialog.bulk
                ? `Удалить ${selectedIds.length} элементов и всё их содержимое? Это действие необратимо.`
                : `Удалить «${selectedItem?.name}» и всё его содержимое? Это действие необратимо.`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialog({ open: false, bulk: false })}
            >
              Отмена
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteConfirmed}
            >
              Удалить
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Preview dialog ── */}
        <Dialog
          open={!!previewItem}
          onClose={() => setPreviewItem(null)}
          maxWidth="md"
          fullWidth
        >
          {previewItem && (
            <>
              <DialogTitle
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  pr: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    overflow: 'hidden',
                  }}
                >
                  {(() => {
                    const ft = getFileType(
                      previewItem.mimeType,
                      previewItem.name
                    );
                    return <ft.Icon sx={{ color: ft.color, flexShrink: 0 }} />;
                  })()}
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {previewItem.name}
                  </Typography>
                  {previewItem.size > 0 && (
                    <Chip label={fmtSize(previewItem.size)} size="small" />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Скачать">
                    <a
                      href={previewItem.url}
                      download={previewItem.name}
                      style={{ color: 'inherit' }}
                    >
                      <IconButton size="small">
                        <DownloadIcon />
                      </IconButton>
                    </a>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setPreviewItem(null)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent
                sx={{ p: previewItem.mimeType?.startsWith('image/') ? 1 : 2 }}
              >
                {previewItem.mimeType?.startsWith('image/') && (
                  <Box
                    component="img"
                    src={previewItem.url}
                    alt={previewItem.name}
                    sx={{ width: '100%', borderRadius: 2, display: 'block' }}
                  />
                )}
                {previewItem.mimeType?.startsWith('video/') && (
                  <Box
                    component="video"
                    src={previewItem.url}
                    controls
                    sx={{ width: '100%', borderRadius: 2 }}
                  />
                )}
                {previewItem.mimeType?.startsWith('audio/') && (
                  <Box sx={{ py: 3, textAlign: 'center' }}>
                    <AudioIcon sx={{ fontSize: 64, color: '#d97706', mb: 2 }} />
                    <Box
                      component="audio"
                      src={previewItem.url}
                      controls
                      sx={{ width: '100%' }}
                    />
                  </Box>
                )}
                {previewItem.mimeType === 'application/pdf' && (
                  <Box
                    component="iframe"
                    src={previewItem.url}
                    sx={{
                      width: '100%',
                      height: '70vh',
                      border: 'none',
                      borderRadius: 2,
                    }}
                    title={previewItem.name}
                  />
                )}
              </DialogContent>
            </>
          )}
        </Dialog>

        {/* ── Generic progress dialog (delete) ── */}
        <Dialog open={progressDialog.open} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {progressDialog.title}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ py: 4, textAlign: 'center' }}>
              {progressDialog.progress < 100 ? (
                <CircularProgress size={50} thickness={4} sx={{ mb: 3 }} />
              ) : (
                <Typography variant="h4" color="success.main" sx={{ mb: 3 }}>
                  ✓
                </Typography>
              )}
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                {progressDialog.progress === 100
                  ? 'Готово!'
                  : progressDialog.title}
              </Typography>
              <Box sx={{ width: '100%', mt: 2, px: 4 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressDialog.progress}
                  sx={{ height: 12, borderRadius: 6, mb: 1 }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {progressDialog.items.length} элем.
                  </Typography>
                  <Typography variant="body2" fontWeight={800}>
                    {progressDialog.progress}%
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  maxHeight: 200,
                  overflow: 'auto',
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'grey.300',
                  textAlign: 'left',
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mb: 1,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontWeight: 700,
                  }}
                >
                  Затронутые файлы
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {progressDialog.items.map((name, idx) => (
                    <Fade in key={idx}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderColor:
                            progressDialog.progress === 100
                              ? 'success.light'
                              : 'grey.200',
                          bgcolor:
                            progressDialog.progress === 100
                              ? 'success.50'
                              : 'background.paper',
                        }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontWeight:
                              progressDialog.progress === 100 ? 700 : 400,
                            color:
                              progressDialog.progress === 100
                                ? 'success.main'
                                : 'text.primary',
                          }}
                        >
                          {progressDialog.progress === 100 ? '✓ ' : ''}
                          {name}
                        </Typography>
                      </Paper>
                    </Fade>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

        {/* ── Move dialog ── */}
        <Dialog
          open={Boolean(moveIds)}
          onClose={() => setMoveIds(null)}
          fullWidth
          maxWidth="xs"
          fullScreen={window.innerWidth < 600}
        >
          <DialogTitle>
            {moveIds && moveIds.length > 1
              ? `Переместить ${moveIds.length} элементов`
              : `Переместить «${
                  (
                    displayItems.find((i) => moveIds && i._id === moveIds[0]) ||
                    selectedItem ||
                    {}
                  ).name || ''
                }»`}
          </DialogTitle>
          <DialogContent dividers>
            <List>
              <ListItem button onClick={() => handleMove(null)}>
                <ListItemIcon>
                  <FolderIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Корневой каталог" />
              </ListItem>
              <Divider />
              {allFolders
                .filter(
                  (f) =>
                    moveIds &&
                    !moveIds.includes(f._id) &&
                    // not into a folder that is being moved (or inside one)
                    !(f.path || []).some((p) => moveIds.includes(String(p._id)))
                )
                .sort((a, b) =>
                  [...(a.path || []).map((p) => p.name), a.name]
                    .join(' / ')
                    .localeCompare(
                      [...(b.path || []).map((p) => p.name), b.name].join(
                        ' / '
                      ),
                      'ru',
                      { numeric: true }
                    )
                )
                .map((folder) => (
                  <ListItem
                    key={folder._id}
                    button
                    onClick={() => handleMove(folder._id)}
                  >
                    <ListItemIcon>
                      <FolderIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={folder.name}
                      secondary={folder.path?.map((p) => p.name).join(' / ')}
                    />
                  </ListItem>
                ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMoveIds(null)}>Отмена</Button>
          </DialogActions>
        </Dialog>

        {/* ── Access dialog (one or many items) ── */}
        <Dialog
          open={Boolean(accessTargets)}
          onClose={() => !accessSaving && setAccessTargets(null)}
          maxWidth="sm"
          fullWidth
          fullScreen={window.innerWidth < 600}
        >
          {accessTargets && (
            <>
              <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>
                Кто видит
                {accessTargets.length === 1
                  ? ` «${accessTargets[0].name}»`
                  : ` · ${accessTargets.length} элементов`}
              </DialogTitle>
              <DialogContent>
                <RadioGroup
                  value={accessForm.mode}
                  onChange={(e) =>
                    setAccessForm({ ...accessForm, mode: e.target.value })
                  }
                  sx={{ mt: 1, mb: 2 }}
                >
                  <FormControlLabel
                    value="public"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          Все учителя
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Видно каждому учителю в библиотеке и в каталоге
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="private"
                    control={<Radio />}
                    sx={{ mt: 1 }}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          Только отмеченные учителя
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          И администраторы. Никого не отметить — только
                          администраторы
                        </Typography>
                      </Box>
                    }
                  />
                  {accessInitial && accessInitial.mode === 'keep' && (
                    <FormControlLabel
                      value="keep"
                      control={<Radio />}
                      sx={{ mt: 1 }}
                      label={
                        <Typography variant="body2">
                          Не менять (сейчас у выбранных по-разному)
                        </Typography>
                      }
                    />
                  )}
                </RadioGroup>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    mb: 0.5,
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={800}>
                    Назначено учителям
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() =>
                        setAccessForm({
                          ...accessForm,
                          teachers: Object.fromEntries(
                            staffTeachers.map((tch) => [tch._id, 'on'])
                          ),
                        })
                      }
                    >
                      Всех
                    </Button>
                    <Button
                      size="small"
                      onClick={() =>
                        setAccessForm({
                          ...accessForm,
                          teachers: Object.fromEntries(
                            staffTeachers.map((tch) => [tch._id, 'off'])
                          ),
                        })
                      }
                    >
                      Никого
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  По назначению работает фильтр «Назначено учителям».
                  {accessForm.mode === 'public'
                    ? ' Пока выбрано «Все учителя», файлы видят все.'
                    : ''}
                </Typography>
                <List
                  dense
                  sx={{
                    maxHeight: 320,
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    py: 0,
                  }}
                >
                  {staffTeachers.map((tch) => {
                    const st = accessForm.teachers[tch._id] || 'off';
                    const next = st === 'on' ? 'off' : 'on';
                    return (
                      <ListItem
                        key={tch._id}
                        button
                        onClick={() =>
                          setAccessForm({
                            ...accessForm,
                            teachers: {
                              ...accessForm.teachers,
                              [tch._id]: next,
                            },
                          })
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            size="small"
                            checked={st === 'on'}
                            indeterminate={st === 'mixed'}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={teacherName(tch)}
                          secondary={
                            st === 'mixed'
                              ? 'у части выбранных — нажмите, чтобы назначить всем'
                              : tch.displayName &&
                                  tch.displayName !== tch.username
                                ? tch.username
                                : null
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>

                {accessTargets.some((i) => i.type === 'folder') && (
                  <FormControlLabel
                    sx={{ mt: 1.5 }}
                    control={
                      <Checkbox
                        checked={accessForm.recursive}
                        onChange={(e) =>
                          setAccessForm({
                            ...accessForm,
                            recursive: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Применить ко всему внутри папок
                      </Typography>
                    }
                  />
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button
                  onClick={() => setAccessTargets(null)}
                  disabled={accessSaving}
                >
                  Отмена
                </Button>
                <Button
                  onClick={handleSaveAccess}
                  variant="contained"
                  disabled={accessSaving || !accessChanges()}
                >
                  {accessSaving ? 'Сохраняем…' : 'Сохранить'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* ── Create folder dialog ── */}
        <Dialog
          open={openFolderDialog}
          onClose={() => setOpenFolderDialog(false)}
        >
          <DialogTitle>Создать папку</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Название папки"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyDown={(e) =>
                e.key === 'Enter' && folderName && handleCreateFolder()
              }
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenFolderDialog(false)}>Отмена</Button>
            <Button
              onClick={handleCreateFolder}
              variant="contained"
              disabled={!folderName}
            >
              Создать
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Rename dialog ── */}
        <Dialog
          open={openRenameDialog}
          onClose={() => setOpenRenameDialog(false)}
        >
          <DialogTitle>Переименовать</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Новое название"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && newName && handleRename()}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRenameDialog(false)}>Отмена</Button>
            <Button
              onClick={handleRename}
              variant="contained"
              disabled={!newName}
            >
              Переименовать
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Upload progress dialog ── */}
        <Dialog open={openUploadDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Загрузка файлов</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 4, textAlign: 'center' }}>
              {uploadProgress < 100 ? (
                <CircularProgress size={50} thickness={4} sx={{ mb: 3 }} />
              ) : (
                <Typography variant="h4" color="success.main" sx={{ mb: 3 }}>
                  ✓
                </Typography>
              )}
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                {uploadProgress === 100
                  ? 'Загрузка завершена!'
                  : 'Загружаем в библиотеку...'}
              </Typography>
              <Box sx={{ width: '100%', mt: 2, px: 4 }}>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ height: 12, borderRadius: 6, mb: 1 }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {uploadFiles.length} файл(ов)
                  </Typography>
                  <Typography variant="body2" fontWeight={800}>
                    {uploadProgress}%
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  minHeight: 150,
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'grey.300',
                  textAlign: 'left',
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontWeight: 700,
                  }}
                >
                  Очередь файлов
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {uploadFiles.map((fileName, idx) => (
                    <Fade
                      in
                      key={idx}
                      style={{ transitionDelay: `${idx * 100}ms` }}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          borderColor:
                            uploadProgress === 100
                              ? 'success.light'
                              : 'grey.200',
                          bgcolor:
                            uploadProgress === 100
                              ? 'success.50'
                              : 'background.paper',
                          transform: `scale(${1 - Math.min(idx * 0.05, 0.2)})`,
                          opacity: 1 - Math.min(idx * 0.2, 0.8),
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {uploadProgress === 100 ? (
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="success.main"
                          >
                            ✓ {fileName}
                          </Typography>
                        ) : (
                          <>
                            <FileIcon fontSize="small" color="action" />
                            <Typography variant="body2" noWrap>
                              {fileName}
                            </Typography>
                          </>
                        )}
                      </Paper>
                    </Fade>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </Container>
    </Layout>
  );
}
